import { get, post, patch, del } from "./index";
import http from "./http";

// types are kept simple, adjust as backend evolves
export interface TeamCheckin {
  id: string;
  title: string;
  checkinTime: string; // ISO string
  created_by: string;
  is_public: boolean;
  roomId: string;
}

export interface TeamNote {
  id: string;
  note: string;
  createdAt: string; // ISO string
  created_by: string;
  is_public: boolean;
  roomId: string;
}

const teamModeApi = {
  // room endpoints
  generateRoomId: async (): Promise<{ roomId: string }> => {
    return get("/team-mode/room-id");
  },
  createSession: async (data: any): Promise<any> => {
    const resp = await post("/team-mode", data);
    return resp;
  },
  getSession: async (id: string): Promise<any> => {
    return get(`/team-mode/${id}`);
  },

  // checkin endpoints
  // backend currently returns { total, checkins: TeamCheckin[] }
  listCheckins: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<{ total?: number; checkins: TeamCheckin[] } | TeamCheckin[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/team-mode/${roomId}/checkins${qs}`);
  },

  createCheckin: async (
    roomId: string,
    data: Partial<Omit<TeamCheckin, "id" | "created_by" | "roomId">>,
  ): Promise<TeamCheckin> => {
    return post(`/team-mode/${roomId}/checkins`, data);
  },

  updateCheckin: async (
    roomId: string,
    checkinId: string,
    data: Partial<Omit<TeamCheckin, "id" | "created_by" | "roomId">>,
  ): Promise<TeamCheckin> => {
    return patch(`/team-mode/${roomId}/checkins/${checkinId}`, data);
  },

  deleteCheckin: async (roomId: string, checkinId: string): Promise<void> => {
    return del(`/team-mode/${roomId}/checkins/${checkinId}`);
  },

  // notes endpoints
  listNotes: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<TeamNote[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/team-mode/${roomId}/notes${qs}`);
  },

  createNote: async (
    roomId: string,
    data: Partial<Omit<TeamNote, "id" | "created_by" | "roomId" | "createdAt">>,
  ): Promise<TeamNote> => {
    return post(`/team-mode/${roomId}/notes`, data);
  },

  updateNote: async (
    roomId: string,
    noteId: string,
    data: Partial<Omit<TeamNote, "id" | "created_by" | "roomId" | "createdAt">>,
  ): Promise<TeamNote> => {
    return patch(`/team-mode/${roomId}/notes/${noteId}`, data);
  },

  deleteNote: async (roomId: string, noteId: string): Promise<void> => {
    return del(`/team-mode/${roomId}/notes/${noteId}`);
  },

  // tasks endpoints
  listTasks: async (
    roomId: string,
    params?: { limit?: number; offset?: number; is_public?: boolean },
  ): Promise<any[]> => {
    const qs = params
      ? "?" +
      new URLSearchParams(
        Object.entries(params as Record<string, any>).reduce(
          (acc, [k, v]) => {
            if (v !== undefined && v !== null) acc[k] = String(v);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString()
      : "";
    return get(`/team-mode/${roomId}/tasks${qs}`);
  },

  createTask: async (
    roomId: string,
    data: { title: string; description?: string; status?: string; is_public?: boolean },
  ): Promise<any> => {
    return post(`/team-mode/${roomId}/tasks`, data);
  },

  updateTask: async (
    roomId: string,
    taskId: string,
    data: { title?: string; description?: string; status?: string; is_public?: boolean },
  ): Promise<any> => {
    return patch(`/team-mode/${roomId}/tasks/${taskId}`, data);
  },

  deleteTask: async (roomId: string, taskId: string): Promise<void> => {
    return del(`/team-mode/${roomId}/tasks/${taskId}`);
  },

  // RTC token endpoint
  getRtcToken: async (teamId: string): Promise<{ rtcToken: string }> => {
    return post(`/team-mode/${teamId}/participants/rtc-token`);
  },

  // Participant join/leave endpoints
  participantJoin: async (roomId: string): Promise<any> => {
    return post(`/team-mode/${roomId}/participants/join`);
  },

  participantLeave: async (roomId: string): Promise<any> => {
    return post(`/team-mode/${roomId}/participants/leave`);
  },

  // Session state endpoints
  updateTimer: async (
    roomId: string,
    data: { action: "start" | "pause" | "end"; },
  ): Promise<any> => {
    return post(`/team-mode/${roomId}/timer`, data);
  },

  endSession: async (roomId: string): Promise<any> => {
    return post(`/team-mode/${roomId}/end`);
  },

  leaveSession: async (roomId: string): Promise<any> => {
    return post(`/team-mode/${roomId}/leave`);
  },
};

export default teamModeApi;
